import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	ImagePlaceholder,
	Skeleton,
	Badge,
} from "@hotelise/ui";

import { PageHeader, SearchInput } from "components/molecules";
import { PayoutSummary } from "components/organisms";

import {
	getProperties,
	getPropertiesCount,
	getPropertiesList,
	getPropertiesLoading,
	getPropertiesTotal,
} from "state/modules/properties";
import { Property } from "models/properties";
import { Contract } from "interfaces/contracts";
import { ComplexFilterType, SearchComplexFilter } from "interfaces/filters";
import ContractsClient from "services/api/contracts";

const SKEL_ROWS = ["a", "b", "c", "d", "e", "f"];
const SKEL_MOBILE_CARDS = ["a", "b", "c", "d", "e", "f"];

type SortKey = 'name' | 'fee' | null;
type SortDir = 'asc' | 'desc';

const SortIcon = ({ active, direction }: { active: boolean; direction: SortDir }) => {
	if (!active) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
	if (direction === 'asc') return <ArrowUp className="h-3 w-3 ml-1" />;
	return <ArrowDown className="h-3 w-3 ml-1" />;
};

const formatPropertyName = (name: string): string =>
	name.replaceAll('-', ' ').replaceAll('_', ' ');

interface EnrichedProperty {
	property: Property;
	contract: Contract | undefined;
}

const PropertiesPage = (): JSX.Element => {
	const propertiesList = useSelector(getPropertiesList);
	const propertiesCount = useSelector(getPropertiesCount);
	const propertiesTotal = useSelector(getPropertiesTotal);
	const isPropertiesLoading = useSelector(getPropertiesLoading);

	const [searchFilter, setSearchFilter] = useState<SearchComplexFilter>({ type: ComplexFilterType.SEARCH, value: null });
	const [contractsMap, setContractsMap] = useState<Map<string, Contract>>(new Map());
	const [sortKey, setSortKey] = useState<SortKey>(null);
	const [sortDir, setSortDir] = useState<SortDir>('asc');

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		dispatch(getProperties());

		ContractsClient.getContracts({ limit: '500', offset: 0 }).then((res) => {
			const contracts: Contract[] = res.data?.content || res.data?.items || res.data || [];
			const map = new Map<string, Contract>();
			contracts.forEach((c) => {
				if (c.propertyId) {
					map.set(c.propertyId, c);
				}
			});
			setContractsMap(map);
		}).catch(() => {
			// Contracts fetch failed silently — Fee % and Payout will show dashes
		});
	}, [dispatch]);

	const toggleSort = (key: 'name' | 'fee') => {
		if (sortKey !== key) {
			setSortKey(key);
			setSortDir('asc');
		} else if (sortDir === 'asc') {
			setSortDir('desc');
		} else {
			setSortKey(null);
			setSortDir('asc');
		}
	};

	const sortedProperties = useMemo(() => {
		let filtered = propertiesList;

		const term = (searchFilter.value || "").toLowerCase();
		if (term) {
			filtered = filtered.filter((p) =>
				p.name?.toLowerCase().includes(term) ||
				p.address?.display?.toLowerCase().includes(term)
			);
		}

		const enriched: EnrichedProperty[] = filtered.map((property) => ({
			property,
			contract: contractsMap.get(property.id),
		}));

		if (sortKey) {
			enriched.sort((a, b) => {
				let cmp = 0;
				if (sortKey === 'name') {
					cmp = (a.property.name || '').localeCompare(b.property.name || '');
				} else if (sortKey === 'fee') {
					const feeA = a.contract?.hoteliseFee ?? -1;
					const feeB = b.contract?.hoteliseFee ?? -1;
					cmp = feeA - feeB;
				}
				return sortDir === 'desc' ? -cmp : cmp;
			});
		}

		return enriched;
	}, [propertiesList, contractsMap, sortKey, sortDir, searchFilter]);


	const handleSearchChange = (updatedFilter: SearchComplexFilter) => {
		setSearchFilter(updatedFilter);
	};

	const handleRowClick = (property: Property) => {
		navigate(`${location.pathname}/${property.id}`);
	};

	const renderDesktopSkeleton = () => (
		<div className="hidden lg:block rounded-md border overflow-hidden">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-[80px]"><Skeleton className="h-4 w-full" /></TableHead>
						<TableHead><Skeleton className="h-4 w-full" /></TableHead>
						<TableHead><Skeleton className="h-4 w-full" /></TableHead>
						<TableHead><Skeleton className="h-4 w-full" /></TableHead>
						<TableHead><Skeleton className="h-4 w-full" /></TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{SKEL_ROWS.map((key) => (
						<TableRow key={key}>
							<TableCell><Skeleton className="w-16 h-12 rounded" /></TableCell>
							<TableCell><Skeleton className="h-4 w-3/4" /></TableCell>
							<TableCell><Skeleton className="h-4 w-full" /></TableCell>
							<TableCell><Skeleton className="h-4 w-12" /></TableCell>
							<TableCell><Skeleton className="h-4 w-20" /></TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);

	const renderMobileSkeleton = () => (
		<div className="lg:hidden space-y-2">
			{SKEL_MOBILE_CARDS.map((key) => (
				<div key={key} className="grid grid-cols-[60px_1fr] gap-x-3 gap-y-0.5 p-3 border border-border rounded-lg items-center">
					<div className="row-span-3">
						<Skeleton className="w-[60px] h-[60px] rounded" />
					</div>
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-3 w-full" />
					<Skeleton className="h-3 w-24" />
				</div>
			))}
		</div>
	);

	const renderLoader = () =>
		propertiesCount <= 0 && isPropertiesLoading ? (
			<>
				{renderDesktopSkeleton()}
				{renderMobileSkeleton()}
			</>
		) : null;

	const renderTableHeader = (): JSX.Element => (
		<TableHeader>
			<TableRow>
				<TableHead className="w-[80px]">Image</TableHead>
				<TableHead>
					<button
						type="button"
						className="inline-flex items-center hover:text-foreground transition-colors"
						onClick={() => toggleSort('name')}
					>
						Name
						<SortIcon active={sortKey === 'name'} direction={sortDir} />
					</button>
				</TableHead>
				<TableHead>Address</TableHead>
				<TableHead>
					<button
						type="button"
						className="inline-flex items-center hover:text-foreground transition-colors"
						onClick={() => toggleSort('fee')}
					>
						Fee %
						<SortIcon active={sortKey === 'fee'} direction={sortDir} />
					</button>
				</TableHead>
				<TableHead>Payout</TableHead>
			</TableRow>
		</TableHeader>
	);

	const renderEmptyState = (message: string) => (
		<>
			<div className="hidden lg:block rounded-md border overflow-hidden">
				<Table>
					{renderTableHeader()}
					<TableBody>
						<TableRow>
							<TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
								{message}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</div>
			<p className="lg:hidden text-sm text-muted-foreground py-4">{message}</p>
		</>
	);

	const renderProperties = () => {
		if (!propertiesList.length && !isPropertiesLoading) {
			return renderEmptyState("No properties found.");
		}

		if (propertiesList.length && !sortedProperties.length) {
			return renderEmptyState("No matching properties.");
		}

		return (
			<div className="overflow-hidden">
				{/* Mobile View */}
				<div className="lg:hidden space-y-2">
					{sortedProperties.map(({ property, contract }, index) => (
						<div
							key={property.id || `property-${index}`}
							onClick={() => handleRowClick(property)}
							className="grid grid-cols-[60px_1fr] gap-x-3 gap-y-0.5 p-3 border border-border rounded-lg items-center transition-colors hover:bg-[#0d1525] cursor-pointer"
							style={{ opacity: !property.listed ? 0.5 : 1 }}
						>
							<div className="flex items-center w-[60px] h-[60px] row-span-3">
								<ImagePlaceholder
									src={property.picture}
									className="w-full h-full rounded object-cover"
									iconSize="h-4 w-4"
									aspectRatio=""
								/>
							</div>
							<div className="flex items-center gap-2">
								<p className="capitalize text-foreground text-sm font-semibold leading-tight m-0">
									{formatPropertyName(property.name)}
								</p>
								{!property.listed && <Badge variant="secondary">Muted</Badge>}
							</div>
							<p className="text-muted-foreground text-xs leading-snug m-0">
								{property.address.display}
							</p>
							<div className="flex items-center gap-2 text-xs text-muted-foreground">
								<span className="tabular-nums">
									{contract?.hoteliseFee != null ? `${contract.hoteliseFee}%` : '—'}
								</span>
								<span>·</span>
								<PayoutSummary channelSplit={contract?.channelSplit} />
							</div>
						</div>
					))}
				</div>

				{/* Desktop View */}
				<div className="hidden lg:block">
					<Table>
						{renderTableHeader()}
						<TableBody>
							{sortedProperties.map(({ property, contract }, index) => (
								<TableRow
									key={property.id || `property-${index}`}
									onClick={() => handleRowClick(property)}
									className={`cursor-pointer hover:bg-muted/40 transition-colors ${!property.listed ? 'opacity-50' : ''}`}
								>
									<TableCell>
										<div className="w-16 h-12">
											<ImagePlaceholder
												src={property.picture}
												className="w-full h-full rounded object-cover"
												iconSize="h-4 w-4"
												aspectRatio=""
											/>
										</div>
									</TableCell>
									<TableCell className="font-medium capitalize">
										<div className="flex items-center gap-2">
											{formatPropertyName(property.name)}
											{!property.listed && <Badge variant="secondary">Muted</Badge>}
										</div>
									</TableCell>
									<TableCell className="text-muted-foreground">
										{property.address.display}
									</TableCell>
									<TableCell className="tabular-nums">
										{contract?.hoteliseFee != null ? `${contract.hoteliseFee}%` : '—'}
									</TableCell>
									<TableCell>
										<PayoutSummary channelSplit={contract?.channelSplit} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</div>
		);
	};

	return (
		<div className="w-full h-screen overflow-auto bg-background sm:pb-24 md:h-auto md:min-h-screen" id="container">
			<PageHeader
				title="Properties"
				description="Browse your properties and identify inactive ones."
			/>
			<div className="flex pb-3 md:flex-col">
				<SearchInput
					filterData={searchFilter}
					changeFilter={handleSearchChange}
					placeholder="Search by name or address"
					background="transparent"
					border="1px solid rgba(148, 163, 184, 0.1)"
					width="320px"
				/>
			</div>

			{renderProperties()}
			{renderLoader()}
		</div>
	);
};

export default PropertiesPage;
